import { useState, useEffect, useMemo } from "react";
import api from "../lib/api";
import DateFilter, { resolveRange } from "../components/DateFilter";
import { Th, Td, Empty, StatusBadge } from "../components/shared";
import { fmtDate } from "../lib/format";
import { ClipboardList, FileText, ShoppingCart, PackageCheck, Clock, AlertTriangle, Boxes, ArrowDownUp } from "lucide-react";

const KPIS = [
  ["requirements_received", "Requirements Received", ClipboardList, "navy"],
  ["requirements_due_today", "Due Today", Clock, "amber"],
  ["requirements_completed", "Completed", PackageCheck, "emerald"],
  ["quotations_requested", "Quotations Requested", FileText, "teal"],
  ["quotations_received", "Quotations Received", FileText, "teal"],
  ["purchases_completed", "Purchases Made", ShoppingCart, "navy"],
  ["materials_received", "Materials Received", PackageCheck, "emerald"],
  ["pending_requirements", "Pending", Clock, "amber"],
  ["overdue_requirements", "Overdue", AlertTriangle, "rose"],
  ["stock_in", "Stock In", Boxes, "emerald"],
  ["stock_out", "Stock Out", ArrowDownUp, "amber"],
  ["stock_updated", "Stock Updated", Boxes, "teal"],
];
const TONE = { navy: "text-navy bg-slate-100", emerald: "text-brand-emerald bg-emerald-50", amber: "text-amber-600 bg-amber-50", rose: "text-rose-600 bg-rose-50", teal: "text-brand-teal bg-teal-50" };

export default function DailyWork() {
  const [preset, setPreset] = useState("Today");
  const [cf, setCf] = useState(""); const [ct, setCt] = useState("");
  const [data, setData] = useState(null);
  const range = useMemo(() => resolveRange(preset, cf, ct), [preset, cf, ct]);

  useEffect(() => {
    api.get("/daily", { params: { date_from: range.from, date_to: range.to } }).then((r) => setData(r.data));
  }, [range.from, range.to]);

  const k = data?.kpis || {};
  return (
    <div className="space-y-6 animate-fade-up">
      <div>
        <h1 className="font-heading text-3xl font-bold text-navy tracking-tight">Daily Procurement Work</h1>
        <p className="text-brand-slate text-sm mt-1">Everything procurement did in the selected period — auto-generated from live records</p>
      </div>
      <div className="bg-white border border-border rounded-xl p-4">
        <DateFilter value={preset} onChange={setPreset} customFrom={cf} customTo={ct} setCustomFrom={setCf} setCustomTo={setCt} />
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-3">
        {KPIS.map(([key, label, Icon, tone]) => (
          <div key={key} data-testid={`daily-kpi-${key}`} className="bg-white rounded-xl border border-border p-4">
            <div className={`w-8 h-8 rounded-lg flex items-center justify-center mb-2 ${TONE[tone]}`}><Icon className="w-4 h-4" /></div>
            <div className="font-heading text-2xl font-bold text-navy tabular-nums">{k[key] ?? 0}</div>
            <div className="text-[11px] text-brand-slate leading-tight mt-0.5">{label}</div>
          </div>
        ))}
      </div>

      <div className="bg-white rounded-xl border border-border overflow-hidden">
        <div className="px-6 py-4 border-b border-border"><h3 className="font-heading font-semibold text-navy">Today's Activity</h3></div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-slate-50 border-b border-border"><tr>{["Time", "Activity", "Requirement / Item", "Vendor", "Status", "By", "Remarks"].map((h) => <Th key={h}>{h}</Th>)}</tr></thead>
            <tbody>
              {!data ? <tr><td colSpan={7} className="py-12 text-center text-brand-slate">Loading…</td></tr>
                : data.activities.length === 0 ? <tr><td colSpan={7}><Empty icon={ClipboardList} msg="No activity in this period" /></td></tr>
                : data.activities.map((a) => (
                  <tr key={a.id} data-testid="activity-row" className="border-b border-border/60 hover:bg-slate-50">
                    <Td className="text-xs text-slate-500">{new Date(a.date).toLocaleString("en-IN", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}</Td>
                    <Td className="font-medium">{a.activity}</Td>
                    <Td className="text-slate-600">{a.req_code ? `${a.req_code} · ` : ""}{a.item || "—"}</Td>
                    <Td className="text-slate-600">{a.vendor || "—"}</Td>
                    <Td>{a.status ? <StatusBadge value={a.status} /> : "—"}</Td>
                    <Td className="text-slate-600">{a.user_name}</Td>
                    <Td className="text-slate-500 text-xs">{a.remarks || "—"}</Td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
